// Campaigns list (ADMIN_UI §3.3). Filter chips for app / status / trigger_type
// + "New campaign" CTA. Row click → /campaigns/$campaignId.

import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { AsyncState } from '../../components/AsyncState.js';
import { Badge } from '../../components/ui/badge.js';
import { Button } from '../../components/ui/button.js';
import { Select } from '../../components/ui/select.js';
import { Table, TBody, TD, TH, THead, TR } from '../../components/ui/table.js';
import { listCampaigns, type CampaignListFilters } from '../../lib/api/campaigns.js';
import type { CampaignRecord } from '../../lib/api/types.js';
import { qk } from '../../lib/query-keys.js';

export const Route = createFileRoute('/_authed/campaigns/')({
  component: CampaignsListPage,
});

const statusVariant: Record<
  CampaignRecord['status'],
  'success' | 'warning' | 'neutral' | 'default'
> = {
  active: 'success',
  paused: 'warning',
  archived: 'neutral',
  draft: 'default',
};

function CampaignsListPage() {
  const [filters, setFilters] = useState<CampaignListFilters>({});
  const query = useQuery({
    queryKey: qk.campaigns.list({ ...filters }),
    queryFn: () => listCampaigns(filters),
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-xl font-semibold text-neutral-900">Campaigns</h2>
        <Link to="/campaigns/new">
          <Button>New campaign</Button>
        </Link>
      </div>

      <div className="mb-4 flex gap-2">
        <Select
          aria-label="Filter by app"
          value={filters.app ?? ''}
          onChange={(e) => setFilters((p) => ({ ...p, app: e.target.value || undefined }))}
        >
          <option value="">All apps</option>
          <option value="sg">sg</option>
          <option value="csg">csg</option>
          <option value="lol">lol</option>
          <option value="wts">wts</option>
          <option value="vcs">vcs</option>
        </Select>
        <Select
          aria-label="Filter by status"
          value={filters.status ?? ''}
          onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value || undefined }))}
        >
          <option value="">Any status</option>
          <option value="draft">draft</option>
          <option value="active">active</option>
          <option value="paused">paused</option>
          <option value="archived">archived</option>
        </Select>
        <Select
          aria-label="Filter by trigger type"
          value={filters.trigger_type ?? ''}
          onChange={(e) => setFilters((p) => ({ ...p, trigger_type: e.target.value || undefined }))}
        >
          <option value="">Any trigger</option>
          <option value="event">event</option>
          <option value="delayed">delayed</option>
          <option value="scheduled">scheduled</option>
        </Select>
      </div>

      <AsyncState
        query={query}
        isEmpty={(d) => d.campaigns.length === 0}
        empty={
          <p className="text-sm text-neutral-500">
            No campaigns match the current filters.{' '}
            <Link to="/campaigns/new" className="text-neutral-900 underline">
              Create one
            </Link>
            .
          </p>
        }
      >
        {(data) => (
          <Table>
            <THead>
              <TR>
                <TH>Name</TH>
                <TH>Key</TH>
                <TH>App</TH>
                <TH>Trigger</TH>
                <TH>Status</TH>
                <TH>Priority</TH>
                <TH>Last sent</TH>
              </TR>
            </THead>
            <TBody>
              {data.campaigns.map((c) => (
                <TR key={c.id} className="cursor-pointer">
                  <TD>
                    <Link
                      to="/campaigns/$campaignId"
                      params={{ campaignId: String(c.id) }}
                      className="hover:underline"
                    >
                      {c.name}
                    </Link>
                  </TD>
                  <TD className="font-mono text-xs">{c.campaign_key}</TD>
                  <TD>{c.app ?? 'all'}</TD>
                  <TD>{c.trigger_type}</TD>
                  <TD>
                    <Badge variant={statusVariant[c.status]}>{c.status}</Badge>
                  </TD>
                  <TD>{c.priority}</TD>
                  <TD className="text-xs text-neutral-500">
                    {c.last_sent_at ? new Date(c.last_sent_at).toLocaleString() : '—'}
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
        )}
      </AsyncState>
    </div>
  );
}
